import React from 'react'
import { withStyles } from '@material-ui/core/styles'
import Button from '@material-ui/core/Button'
import Menu from '@material-ui/core/Menu'
import MenuItem from '@material-ui/core/MenuItem'
import ListItemIcon from '@material-ui/core/ListItemIcon'
import ListItemText from '@material-ui/core/ListItemText'
import { Typography, Icon } from '@material-ui/core'
import { FormattedMessage } from 'react-intl'
import Assets from 'app/configs/assetConfigs'
import DoNotTranslateIcon from 'app/components/richTextEditor/DoNotTranslateIcon'
import { makeStyles } from '@material-ui/styles'
import { useAppSettings, getSkipTranslation } from 'commons/useAppSettings'

const StyledMenu = withStyles({
  paper: {
    border: '1px solid #d3d4d5',
  },
})((props) => {
  return (
    <Menu
      elevation={0}
      anchorOrigin={{
        vertical: 'bottom',
        horizontal: 'center',
      }}
      transformOrigin={{
        vertical: 'top',
        horizontal: 'center',
      }}
      getContentAnchorEl={null}
      PaperProps={{
        className: 'w-full sm:w-xs',
        style: {
          maxHeight: 400,
        },
      }}
      {...props}
    />
  )
})

const StyledMenuItem = withStyles((theme) => ({
  root: {
    '&:focus': {
      backgroundColor: theme.palette.common.white,
      '& .MuiListItemIcon-root, & .MuiListItemText-primary': {
        color: theme.palette.common.white,
      },
    },
    pointerEvents: 'none',
    '& .grammarlyIcon': {
      maxWidth: '40px',
      maxHeight: '40px',
      width: '100%',
      height: '100%',
    },
    '& .chromeIcon': {
      maxWidth: '40px',
      maxHeight: '40px',
      width: '100%',
      height: '100%',
    },
  },
}))(MenuItem)

const useStyles = makeStyles((theme) => ({
  doNotTranslateIcon: {
    marginBottom: -6,
  },
  doNotTranslateHighlight: theme.palette.richTextEditor.doNotTranslate,
}))

const createFormatTipValues = (classes) => ({
  'do-not-translate-icon': () => (
    <DoNotTranslateIcon className={classes.doNotTranslateIcon} />
  ),
  'do-not-translate-highlight': (text) => (
    <span className={classes.doNotTranslateHighlight}>{text}</span>
  ),
  b: (text) => <span className="font-bold">{text}</span>,
  newline: <br />,
})

export default function CustomizedMenus(props) {
  const { className } = props
  const [anchorEl, setAnchorEl] = React.useState(null)

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget)
  }

  const handleClose = () => {
    setAnchorEl(null)
  }

  const classes = useStyles()
  const formatTipValues = createFormatTipValues(classes)
  const isSkipTranslation = getSkipTranslation(useAppSettings())
  const tips = [
    !isSkipTranslation && (
      <StyledMenuItem
        className="flex flex-row items-start pb-16 border-b-1 border-solid border-gray-400"
        key="doNoTranslate"
      >
        <ListItemIcon>
          <img
            className="w-40"
            src={Assets.tips.doNotTranslate}
            alt="notifyme"
          />
        </ListItemIcon>
        <ListItemText
          className="mt-0"
          primary={
            <>
              <Typography className="font-bold whitespace-normal">
                <FormattedMessage
                  id="notification.tips1.line1"
                  values={formatTipValues}
                />
              </Typography>
              <Typography className="whitespace-normal">
                <FormattedMessage
                  id="notification.tips1.line2"
                  values={formatTipValues}
                />
              </Typography>
              <Typography className="whitespace-normal">
                <FormattedMessage id="notification.tips1.line3" />
              </Typography>
              <Typography className="whitespace-normal">
                <FormattedMessage
                  id="notification.tips1.line4"
                  values={formatTipValues}
                />
              </Typography>
              <Typography className="whitespace-normal">
                <FormattedMessage
                  id="notification.tips1.line5"
                  values={formatTipValues}
                />
              </Typography>
            </>
          }
        />
      </StyledMenuItem>
    ),
    <StyledMenuItem key="browser" className="flex flex-row items-start mt-8">
      <ListItemIcon>
        <img className="chromeIcon" src={Assets.tips.chrome} alt="chrome" />
      </ListItemIcon>
      <ListItemText
        className="mt-0"
        primary={
          <>
            <Typography className="font-bold whitespace-normal">
              <FormattedMessage
                id="notification.tips2.line1"
                values={formatTipValues}
              />
            </Typography>
            <Typography className="whitespace-normal">
              <FormattedMessage
                id="notification.tips2.line2"
                values={formatTipValues}
              />
            </Typography>
          </>
        }
      />
    </StyledMenuItem>,
    <StyledMenuItem className="flex flex-row items-start mt-8" key="grammarly">
      <ListItemIcon>
        <img
          className="grammarlyIcon"
          src={Assets.tips.grammarly}
          alt="grammarly"
        />
      </ListItemIcon>
      <ListItemText
        className="mt-0"
        primary={
          <>
            <Typography className="font-bold whitespace-normal">
              <FormattedMessage
                id="notification.tips3.line1"
                values={formatTipValues}
              />
            </Typography>
            <Typography className="whitespace-normal">
              <FormattedMessage
                id="notification.tips3.line2"
                values={formatTipValues}
              />
            </Typography>
          </>
        }
      />
    </StyledMenuItem>,
  ]

  return (
    <div className={className}>
      <Button
        variant="outlined"
        className="normal-case w-full"
        aria-haspopup="true"
        onClick={handleClick}
        style={{
          paddingLeft: '0.85rem',
          paddingRight: '1rem',
          paddingTop: '1.2rem',
          paddingBottom: '1.2rem',
        }}
        data-testid="notiTips"
      >
        <div className="flex flex-row justify-between w-full">
          <div className="flex flex-row">
            <img
              src={Assets.tips.alert}
              width={25}
              height={25}
              alt="info-only-type"
            />
            <div className="ltr:ml-12 rtl:mr-12">
              <FormattedMessage
                id="notification.tips.placeholder"
                values={{ tipsCount: tips.filter((t) => t).length }}
              />
            </div>
          </div>
          <Icon>arrow_drop_down</Icon>
        </div>
      </Button>
      <StyledMenu
        className="mt-5"
        id="customized-menu"
        anchorEl={anchorEl}
        keepMounted
        open={Boolean(anchorEl)}
        onClose={handleClose}
      >
        {tips}
      </StyledMenu>
    </div>
  )
}
